import { Question, Section } from '../../types/form';
import { getConditionalAttachmentOrderDetailKeys } from '../../utils/form';

export interface CarriedFormResult {
  /** The carried questionnaire answers, filtered to the new schema. */
  form: Record<string, any>;
  /** Number of ANSWERS that could not be carried (removed/renamed/retyped questions). */
  droppedCount: number;
}

const collectQuestions = (sections: Section[]): Question[] =>
  (sections ?? []).flatMap((section) => section.questions ?? []);

// Default conditional-attachment side-key suffixes (see getConditionalAttachmentOrderDetailKeys).
const DEFAULT_ATTACHMENT_SIDE_KEY = /(PhotoAttachments|FileAttachments)$/;

const collectAttachmentSideKeys = (questions: Question[]): Set<string> => {
  const keys = new Set<string>();
  for (const question of questions) {
    if (!question.conditionalAttachment?.attachmentConfig) continue;
    const { photoOrderDetailKey, fileOrderDetailKey } =
      getConditionalAttachmentOrderDetailKeys(question);
    keys.add(photoOrderDetailKey);
    keys.add(fileOrderDetailKey);
  }
  return keys;
};

/**
 * Version-aware copy of a parent order's questionnaire answers onto a new order.
 *
 * An answer is carried only when its question id still exists in the NEW schema AND the
 * question `type` is unchanged from the OLD schema — a retyped question would carry a
 * value of the wrong shape, which would render incorrectly or fail validation.
 *
 * Attachment side-keys (`${qId}PhotoAttachments` / `FileAttachments`, or a question's
 * custom `orderDetailKey`) are dropped entirely: they store filenames embedding the OLD
 * order's id, so they are stale on the new order. The copied images are re-resolved by
 * TAG against the new order's file list instead (render and validation are tag-based).
 * Dropped side-keys are NOT counted — they are not answers.
 *
 * Pure and deterministic: takes the resolved section arrays, returns the filtered form
 * plus the count of dropped answers (for the "X fields could not be carried" notice).
 */
export function buildCarriedForm(
  previousForm: Record<string, any> | undefined | null,
  oldSections: Section[],
  newSections: Section[],
): CarriedFormResult {
  if (!previousForm || Object.keys(previousForm).length === 0) {
    return { form: {}, droppedCount: 0 };
  }

  const oldQuestions = collectQuestions(oldSections);
  const newQuestions = collectQuestions(newSections);
  const oldTypeById = new Map(oldQuestions.map((q) => [q.id, q.type]));
  const newTypeById = new Map(newQuestions.map((q) => [q.id, q.type]));
  const attachmentSideKeys = new Set<string>([
    ...collectAttachmentSideKeys(oldQuestions),
    ...collectAttachmentSideKeys(newQuestions),
  ]);

  const isAttachmentSideKey = (key: string): boolean =>
    attachmentSideKeys.has(key) || DEFAULT_ATTACHMENT_SIDE_KEY.test(key);

  const form: Record<string, any> = {};
  let droppedCount = 0;

  for (const [key, value] of Object.entries(previousForm)) {
    if (isAttachmentSideKey(key)) continue; // stale filenames; images re-resolve by tag

    const newType = newTypeById.get(key);
    if (newType === undefined) {
      droppedCount++; // question removed or renamed in the new version
      continue;
    }
    const oldType = oldTypeById.get(key);
    if (oldType !== undefined && oldType !== newType) {
      droppedCount++; // question retyped — carried value no longer valid
      continue;
    }
    form[key] = value;
  }

  return { form, droppedCount };
}
